// src/contexts/PromptQueueContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';

const PromptQueueContext = createContext();

export const PromptQueueProvider = ({ children }) => {
  const [queue, setQueue] = useState([]);
  const [currentPrompt, setCurrentPrompt] = useState(null);
  const queueRef = useRef([]);
  const currentPromptRef = useRef(null);

  // Update refs when state changes
  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);

  useEffect(() => {
    currentPromptRef.current = currentPrompt;
  }, [currentPrompt]);

  const enqueuePrompt = useCallback((PromptComponent, props = {}) => {
    // Check if this exact component is already in the queue
    const isAlreadyInQueue = queueRef.current.some(
      (item) => item.component === PromptComponent,
    );

    // Check if this component is currently being displayed
    const isCurrentlyDisplayed =
      currentPromptRef.current?.component === PromptComponent;

    if (!isAlreadyInQueue && !isCurrentlyDisplayed) {
      setQueue((prev) => [...prev, { component: PromptComponent, props }]);
    }
  }, []);

  const showNextPrompt = useCallback(() => {
    setQueue((prev) => {
      const [, ...rest] = prev;
      return rest;
    });
  }, []);

  useEffect(() => {
    if (!currentPrompt && queue.length > 0) {
      const nextPrompt = queue[0];
      setCurrentPrompt(nextPrompt);
    } else if (currentPrompt && queue.length === 0) {
      setCurrentPrompt(null);
    }
  }, [queue, currentPrompt]);

  const closeCurrentPrompt = useCallback(() => {
    setCurrentPrompt(null);
    showNextPrompt();
  }, [showNextPrompt]);

  // Cleanup function to clear queue when component unmounts
  useEffect(() => {
    return () => {
      setQueue([]);
      setCurrentPrompt(null);
    };
  }, []);

  return (
    <PromptQueueContext.Provider value={{ enqueuePrompt }}>
      {children}
      {!!currentPrompt && (
        <currentPrompt.component
          {...currentPrompt.props}
          isOpen={true}
          onClose={closeCurrentPrompt}
        />
      )}
    </PromptQueueContext.Provider>
  );
};

export const usePromptQueue = () => useContext(PromptQueueContext);
